import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Modal } from "react-bootstrap";
import {
  BsNewspaper,
  BsBookHalf,
  BsCalendarCheckFill,
  BsGrid3X3GapFill,
  BsArrowRightShort,
  BsXLg,
  BsGeoAltFill,
} from "react-icons/bs";

const NewsStories = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title =
      "Corporate Press Releases & Impact Chronicles | NGO Waters";
  }, []);

  const newsData = [
    {
      id: 1,
      title: "Ponia Food & Beverage Activates Village Boring Pipelines",
      image:
        "https://images.unsplash.com/photo-1541944743827-e04aa6427c33?auto=format&fit=crop&q=80&w=800",
      description:
        "Deploying structural high-capacity underground boring systems and filtration matrices across regional rural sectors in Aligarh.",
      content:
        "Under the executive directive of Ponia Food And Beverage Private Limited, our engineering cell has successfully commissioned 3 advanced high-yield deep tube well boring nodes. Financed directly by our standard 50% commercial revenue allocation framework, this installation delivers safe, trace-mineral balanced drinking water to over 1,200 regional family clusters who previously faced deep clean-water scarcity risks. Every extraction cluster features integrated stainless-steel mechanical safety taps to prevent ground surface cross-contamination.",
      date: "May 14, 2026",
      tag: "Infrastructure Deployment",
    },
    {
      id: 2,
      title: "Commercial Retail Network Expansion Syncs with Local Hubs",
      image:
        "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&q=80&w=800",
      description:
        "Partnering with regional distribution nodes to establish direct pipeline logs and stabilize commercial supply matrices.",
      content:
        "To guarantee continuous delivery metrics across Uttar Pradesh, our logistical operations team has completed comprehensive strategic integration protocols with local franchise terminals. By implementing a decentralized inventory allocation structure, we have successfully optimized fleet transport tracks, reducing transit energy outputs by 18%. This enterprise framework empowers our trade partners to access immediate dispatch registers while securing stable wholesale pricing caps.",
      date: "June 02, 2026",
      tag: "Logistical Grid",
    },
    {
      id: 3,
      title: "Ecosystem Quality Audit Validates BIS Mineral Consistency",
      image:
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800",
      description:
        "Our automated bottling laboratory clears comprehensive physical and chemical safety criteria under IS 14543 standards.",
      content:
        "Transparency remains a baseline corporate standard at our production facilities. Our centralized laboratory cell recently finalized a complete multi-tier microbiological and element audit. The metrics explicitly validate that our reverse osmosis, UV exposure, and micro-filtration pipelines maintain perfect compliance with IS 14543 mineral requirements. This verification confirms that every batch distributed across institutional markets satisfies the peak criteria of pure, structured micro-minerals.",
      date: "June 12, 2026",
      tag: "Quality Compliance",
    },
    {
      id: 4,
      title: "Solar-Powered Hydro-Purifiers Deployed in Off-Grid Belts",
      image:
        "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&q=80&w=800",
      description:
        "Integrating solar photovoltaic panels with filtration cells to ensure constant water production in energy-scarce tracks.",
      content:
        "In alignment with sustainable engineering values, NGO Waters has launched its first array of independent, solar-powered multi-stage purification kiosks. These standalone arrays harness local photovoltaic grids to actuate chemical-free filtration cycles without requiring external high-voltage infrastructure. Deployed strategically in regional grid-scarce tracks around Gonda block, these units provide zero-cost emergency filtration buffers for public communities while operating with near-zero friction overheads.",
      date: "June 18, 2026",
      tag: "Sustainable Tech",
    },
  ];

  const handleShowModal = (news) => {
    setSelectedNews(news);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };
  const microSpring = { type: "spring", stiffness: 300, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Ambient Visual Blur Layers */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "600px",
          height: "600px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-200px",
          right: "-40px",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "500px",
          height: "500px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "10%",
          left: "-60px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Production-Grade SEO / AEO News Article Structured Data Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline:
            "NGO Waters Corporate Operations and Social Impact Deployments",
          description:
            "Official chronological records of water distribution setups, factory quality audits, and community boring programs by Ponia Food And Beverage Pvt. Ltd.",
          image: newsData[0].image,
          datePublished: "2026-06-19",
          author: {
            "@type": "Organization",
            name: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
            duns: "CIN: U11043UP2024PTC213517",
          },
        })}
      </script>

      {/* ================= HEADER SECTION ================= */}
      <section className="container py-4 position-relative z-1 mb-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(26, 60, 107, 0.06)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.72rem",
              borderRadius: "6px",
            }}
          >
            Press Logs & Field Despatches
          </span>
          <h1
            className="display-5 fw-bold mb-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-1.5px" }}
          >
            Ecosystem Chronicles
          </h1>
          <p
            className="text-secondary small mx-auto mb-0"
            style={{ maxWidth: "560px", lineHeight: "1.65" }}
          >
            Verifiable chronological briefs mapping our automated bottling
            operations, distribution safety checkpoints, and regional
            infrastructure allocations.
          </p>
        </motion.div>
      </section>

      {/* ================= CHRONICLE CARDS GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <div className="row g-4 justify-content-center">
          {newsData.map((news) => (
            <div className="col-md-6 col-xl-6" key={news.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={fluidSpring}
                whileHover={{
                  y: -6,
                  boxShadow: "0px 12px 35px rgba(26, 60, 107, 0.03)",
                }}
                className="card border-0 h-100 bg-white overflow-hidden d-flex flex-column justify-content-between"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.025)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div>
                  {/* Image Element Wrapper */}
                  <div
                    className="position-relative overflow-hidden"
                    style={{ height: "220px" }}
                  >
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <span
                      className="position-absolute top-3 start-3 badge text-white bg-dark px-2.5 py-1.5 small fw-semibold"
                      style={{
                        borderRadius: "6px",
                        fontSize: "0.7rem",
                        backdropFilter: "blur(4px)",
                        backgroundColor: "rgba(0,0,0,0.65)",
                      }}
                    >
                      {news.tag}
                    </span>
                  </div>

                  {/* Content Framework Node */}
                  <div className="p-4">
                    <div
                      className="d-flex align-items-center gap-1.5 text-muted mb-2"
                      style={{ fontSize: "0.75rem" }}
                    >
                      <BsCalendarCheckFill className="small" />
                      <span>{news.date}</span>
                      <span className="mx-1">•</span>
                      <BsGeoAltFill className="small text-secondary" />
                      <span>Aligarh Grid</span>
                    </div>

                    <h5
                      className="fw-bold mb-2 text-dark"
                      style={{
                        fontSize: "1.15rem",
                        letterSpacing: "-0.3px",
                        lineHeight: "1.4",
                      }}
                    >
                      {news.title}
                    </h5>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.6" }}
                    >
                      {news.description}
                    </p>
                  </div>
                </div>

                {/* Footer Action Node */}
                <div className="px-4 pb-4 bg-transparent border-0">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={microSpring}
                    onClick={() => handleShowModal(news)}
                    className="btn btn-outline-dark w-100 py-2 d-flex align-items-center justify-content-center gap-1 fw-semibold"
                    style={{ borderRadius: "10px", fontSize: "0.82rem" }}
                  >
                    Examine Documentation <BsArrowRightShort className="fs-5" />
                  </motion.button>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= TRANSPARENT CORE OPERATION SUMMARY ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div
              className="p-4 p-md-5 bg-white border"
              style={{ borderRadius: "24px", borderColor: "rgba(0,0,0,0.03)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <BsGrid3X3GapFill
                  style={{ color: "var(--brand-mineral-aqua)" }}
                />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Institutional Fund Allocation Map
                </h4>
              </div>
              <p
                className="text-secondary small mb-0"
                style={{ lineHeight: "1.7" }}
              >
                Every data block logging operational progress across our grid
                remains verifiable under the strict validation framework of{" "}
                <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong> (CIN:
                U11043UP2024PTC213517), rooted in Aligarh, Uttar Pradesh. Our
                business configuration converts commercial market gains cleanly
                into infrastructure solutions—directing exactly half of net
                operational resources to deploy deep boring assets, supply
                stainless filtration equipment, and establish emergency water
                access kiosks for rural public centers at zero liability to
                local populations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= HIGH-END MODAL COMPONENT (BOOTSTRAP + MOTION INTERCEPT) ================= */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        dialogClassName="custom-corporate-modal"
        contentClassName="border-0"
        style={{
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.15)",
        }}
      >
        {selectedNews && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "28px",
              overflow: "hidden",
            }}
          >
            {/* Modal Header Grid */}
            <div className="p-4 border-bottom border-light d-flex align-items-start justify-content-between">
              <div>
                <span
                  className="badge bg-light text-dark px-2.5 py-1.5 mb-2 rounded fw-semibold text-uppercase"
                  style={{ fontSize: "0.68rem", letterSpacing: "0.5px" }}
                >
                  {selectedNews.tag}
                </span>
                <h4
                  className="fw-bold m-0 text-dark"
                  style={{
                    color: "var(--brand-deep-blue)",
                    letterSpacing: "-0.5px",
                    lineHeight: "1.35",
                  }}
                >
                  {selectedNews.title}
                </h4>
                <div className="text-muted small mt-2 d-flex align-items-center gap-2">
                  <span>Logged: {selectedNews.date}</span>
                  <span>•</span>
                  <span>Compliance Desk Verified</span>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                transition={microSpring}
                onClick={handleCloseModal}
                className="btn bg-light rounded-circle p-2 d-flex align-items-center justify-content-center border-0 ms-2"
                style={{ width: "36px", height: "36px" }}
              >
                <BsXLg className="text-secondary small" />
              </motion.button>
            </div>

            {/* Modal Body Framework */}
            <div
              className="p-4 p-md-5"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              <div
                className="rounded-4 overflow-hidden mb-4 border"
                style={{ maxHeight: "350px" }}
              >
                <img
                  src={selectedNews.image}
                  alt={selectedNews.title}
                  className="w-100 h-100"
                  style={{ objectFit: "cover" }}
                />
              </div>

              <div className="d-flex align-items-center gap-2 mb-3 text-dark fw-bold">
                <BsBookHalf className="text-muted" />
                <span style={{ fontSize: "0.95rem" }}>
                  Official Briefing Dossier
                </span>
              </div>

              <p
                className="text-secondary mb-0"
                style={{
                  fontSize: "0.92rem",
                  lineHeight: "1.8",
                  textAlign: "justify",
                }}
              >
                {selectedNews.content}
              </p>
            </div>

            {/* Modal Footer Grid */}
            <div className="p-4 bg-light d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
              <span
                className="small text-muted fw-semibold"
                style={{ fontSize: "0.72rem" }}
              >
                PONIA FOOD AND BEVERAGE PVT. LTD. • CORPORATE COGNIZANCE
              </span>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={microSpring}
                onClick={handleCloseModal}
                className="btn btn-dark btn-sm px-4 py-2 fw-semibold"
                style={{ borderRadius: "8px" }}
              >
                Dismiss Logs
              </motion.button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
};

export default NewsStories;
